import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { TreeStructure, UsersThree, X, LinkSimple, Trash } from '@phosphor-icons/react';

const Badge = ({ children, tone = 'slate' }) => (
  <span className={`font-mono-label text-[9px] uppercase px-2 py-0.5 rounded bg-${tone}-100 text-${tone}-700`}>{children}</span>
);
const Card = ({ children, className = '', ...p }) => (
  <div className={`bg-white border border-slate-200 rounded-lg p-4 ${className}`} {...p}>{children}</div>
);
const Label = ({ children }) => <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">{children}</p>;

const Person360 = ({ pid, onClose }) => {
  const qc = useQueryClient();
  const { data: p } = useQuery({ queryKey: ['person', pid], queryFn: () => api.get(`/identity/persons/${pid}`).then((r) => r.data) });
  const unlink = useMutation({ mutationFn: (eid) => api.delete(`/identity/persons/${pid}/link/${eid}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['person', pid] }); qc.invalidateQueries({ queryKey: ['persons'] }); toast.success('Unlinked'); }, onError: (e) => toast.error(errMsg(e)) });
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="person-360">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-5">
        <div className="flex items-center justify-between mb-3"><h3 className="font-display text-lg font-bold">{p?.full_name || 'Person'}</h3><button onClick={onClose}><X /></button></div>
        {!p ? <p className="text-sm text-slate-400">Loading...</p> : (
          <>
            <div className="text-sm text-slate-500 mb-3">{p.primary_email}{p.national_id ? ` · ${p.national_id}` : ''}</div>
            {p.roles?.length > 0 && <div className="flex gap-1 flex-wrap mb-3">{p.roles.map((r) => <Badge key={r} tone="blue">{r}</Badge>)}</div>}
            <Label>Linked identities ({p.links?.length || 0})</Label>
            {(p.links || []).map((l) => (
              <div key={l.entity_id} className="flex items-center gap-2 py-2 border-t border-slate-100 text-sm" data-testid={`link-${l.entity_id}`}>
                <Badge tone={l.entity_type === 'student' ? 'green' : 'blue'}>{l.entity_type}</Badge>
                <span className="font-medium">{l.label}</span>
                <span className="text-xs text-slate-400">{l.role}</span>
                {l.status && <Badge tone={l.status === 'active' ? 'green' : l.status === 'missing' ? 'rose' : 'slate'}>{l.status}</Badge>}
                <button className="ml-auto text-rose-500" onClick={() => unlink.mutate(l.entity_id)} data-testid={`unlink-${l.entity_id}`}><Trash /></button>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

const PersonsTab = () => {
  const qc = useQueryClient();
  const { data: persons } = useQuery({ queryKey: ['persons'], queryFn: () => api.get('/identity/persons').then((r) => r.data) });
  const [open, setOpen] = useState(null);
  const [f, setF] = useState({ full_name: '', primary_email: '' });
  const create = useMutation({ mutationFn: () => api.post('/identity/persons', f), onSuccess: () => { qc.invalidateQueries({ queryKey: ['persons'] }); setF({ full_name: '', primary_email: '' }); toast.success('Person created'); }, onError: (e) => toast.error(errMsg(e)) });
  return (
    <div data-testid="ig-persons">
      <Card className="mb-4">
        <Label>Create a person</Label>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Full name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="person-name" />
          <Input placeholder="Primary email" value={f.primary_email} onChange={(e) => setF({ ...f, primary_email: e.target.value })} />
        </div>
        <Button className="mt-3" size="sm" onClick={() => create.mutate()} disabled={!f.full_name || create.isPending} data-testid="person-create-btn">Create</Button>
      </Card>
      {(persons || []).length === 0 && <Card><p className="text-sm text-slate-400">No persons yet. Use Suggestions to auto-link records that share an email.</p></Card>}
      <div className="space-y-2">
        {(persons || []).map((p) => (
          <button key={p.id} onClick={() => setOpen(p.id)} className="w-full text-left" data-testid={`person-${p.id}`}>
            <Card className="hover:border-blue-300">
              <div className="flex items-center gap-3 flex-wrap text-sm">
                <UsersThree className="text-blue-600" />
                <span className="font-semibold">{p.full_name}</span>
                {p.primary_email && <span className="text-xs text-slate-400">{p.primary_email}</span>}
                <span className="ml-auto flex gap-1">{(p.link_types || []).map((t) => <Badge key={t} tone={t === 'student' ? 'green' : 'blue'}>{t}</Badge>)}<Badge tone="slate">{p.link_count} linked</Badge></span>
              </div>
            </Card>
          </button>
        ))}
      </div>
      {open && <Person360 pid={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

const SuggestionsTab = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['ig-suggestions'], queryFn: () => api.get('/identity/suggestions').then((r) => r.data) });
  const linkAll = useMutation({
    mutationFn: async (grp) => {
      const p = await api.post('/identity/persons', {
        full_name: grp.candidates[0].label, primary_email: grp.email,
        links: grp.candidates.map((c) => ({ entity_type: c.entity_type, entity_id: c.entity_id })),
      });
      return p.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ig-suggestions', 'persons'] }); toast.success('Linked as one person'); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div data-testid="ig-suggestions">
      <p className="text-sm text-slate-500 mb-3">Records across staff accounts and student records that share an email — likely the same person holding multiple roles.</p>
      {(data?.suggestions || []).length === 0 && <Card><p className="text-sm text-slate-400">No link suggestions right now.</p></Card>}
      <div className="space-y-2">
        {(data?.suggestions || []).map((g) => (
          <Card key={g.email} data-testid={`suggestion-${g.email}`}>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="font-mono text-xs bg-slate-50 border border-slate-100 rounded px-1.5 py-0.5">{g.email}</span>
              <Button size="sm" className="ml-auto" onClick={() => linkAll.mutate(g)} data-testid="suggestion-link-btn"><LinkSimple className="mr-1" /> Link as one person</Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              {g.candidates.map((c) => (
                <span key={c.entity_id} className="flex items-center gap-1 text-xs bg-slate-50 border border-slate-100 rounded px-2 py-1">
                  <Badge tone={c.entity_type === 'student' ? 'green' : 'blue'}>{c.entity_type}</Badge>{c.label} <span className="text-slate-400">{c.role}</span>
                </span>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default function IdentityGraph() {
  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto" data-testid="identity-graph-page">
      <div className="flex items-center gap-3 mb-1"><TreeStructure className="text-blue-600" size={26} /><h1 className="font-display text-2xl font-extrabold">Identity Graph</h1></div>
      <p className="text-sm text-slate-500 mb-4">One person, many roles. Link a student, staff account and guardian into a single human record — an additive overlay that never touches sign-in.</p>
      <Tabs defaultValue="persons">
        <TabsList className="mb-4">
          <TabsTrigger value="persons" data-testid="tab-persons">Persons</TabsTrigger>
          <TabsTrigger value="suggestions" data-testid="tab-suggestions">Suggestions</TabsTrigger>
        </TabsList>
        <TabsContent value="persons"><PersonsTab /></TabsContent>
        <TabsContent value="suggestions"><SuggestionsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
